import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import { Box, IconButton, Typography } from '@mui/material';
import DropDownInput from 'components/DropDownInput';
import InputField from 'components/InputField';
import { ToolTip } from 'components/Tooltip/ToolTip';
import React, { FC } from 'react';
import strings from '../../../../../../common/Translation/Translate';
import { VenueConfigEmptyState } from '../../VenueConfigEmptyState';
import { VenueConfigSectionAction } from '../../VenueConfigSectionAction';
import {
  venueConfigDeleteIconProps,
  venueConfigTableDataRowSx,
  venueConfigTableHeaderCellSx,
  venueConfigTableHeaderRowSx,
  venueConfigTableShellSx,
} from '../../venueConfigTableStyles';
import { DropdownOption, priceDropDownDefaults, PricePackageFieldErrors, PricePackageRow, priceInlineFieldSx } from './priceFormUtils';

type PricePackageCardsProps = {
  rows: PricePackageRow[];
  packageOptions: DropdownOption[];
  errors?: Record<number, PricePackageFieldErrors>;
  onChange: (index: number, field: keyof PricePackageRow, value: any) => void;
  onPackageSelect: (index: number, option: DropdownOption | undefined) => void;
  onBlur: (index: number) => void;
  onDelete: (index: number) => void;
  onAdd: () => void;
};

export const PricePackageCards: FC<PricePackageCardsProps> = ({
  rows,
  packageOptions,
  errors = {},
  onChange,
  onPackageSelect,
  onBlur,
  onDelete,
  onAdd,
}) => (
  <>
    <Box sx={{ mb: 1.5 }}>
      <VenueConfigSectionAction label={strings.venueConfig_price_addPackage} onClick={onAdd} />
    </Box>
    {rows.length === 0 ? (
      <VenueConfigEmptyState message={strings.venueConfig_price_packages_empty} />
    ) : (
      <Box sx={venueConfigTableShellSx}>
        <Box sx={venueConfigTableHeaderRowSx}>
          <Typography {...venueConfigTableHeaderCellSx} sx={{ flex: 2, minWidth: 200 }}>
            {strings.venueConfig_price_columnPricePackage}
          </Typography>
          <Typography {...venueConfigTableHeaderCellSx} sx={{ flex: 1, minWidth: 140 }}>
            {strings.venueConfig_price_columnPricePerPerson}
          </Typography>
          <Box sx={{ width: 40, flexShrink: 0 }} />
        </Box>
        {rows.map((row, index) => {
          const fieldErrors = errors[index] ?? {};
          const packageError = Boolean(fieldErrors.pricePackageId);
          const priceError = Boolean(fieldErrors.pricePerPerson);

          return (
            <Box key={row.id ?? `package-${index}`} sx={venueConfigTableDataRowSx}>
              <Box sx={{ ...priceInlineFieldSx, flex: 2, minWidth: 200 }}>
                <DropDownInput
                  {...priceDropDownDefaults}
                  label={strings.venueConfig_price_pricePackage}
                  size="small"
                  field={`pricePackageId-${index}`}
                  id={`pricePackageId-${index}`}
                  inputValue={row.pricePackageId ?? ''}
                  options={packageOptions}
                  placeholder={strings.venueConfig_price_selectPackage}
                  hasError={packageError}
                  fieldError={packageError}
                  onChange={(value: string | number) => {
                    const opt = packageOptions.find(o => o.value === value);
                    onPackageSelect(index, opt);
                    onBlur(index);
                  }}
                />
              </Box>
              <Box sx={{ ...priceInlineFieldSx, flex: 1, minWidth: 140 }}>
                <InputField
                  label={strings.venueConfig_price_pricePerPerson}
                  size="small"
                  typeValue="number"
                  field={`pricePerPerson-${index}`}
                  inputValue={row.pricePerPerson != null ? String(row.pricePerPerson) : ''}
                  hasError={priceError}
                  fieldError={priceError}
                  inputProps={{ min: 0, step: 'any' }}
                  textChange={value => {
                    onChange(index, 'pricePerPerson', value);
                    onBlur(index);
                  }}
                />
              </Box>
              <ToolTip title={strings.deleteText}>
                <IconButton
                  size="small"
                  onClick={() => onDelete(index)}
                  aria-label="delete price package"
                  sx={{ mt: 0.5 }}>
                  <DeleteOutlineOutlinedIcon {...venueConfigDeleteIconProps} />
                </IconButton>
              </ToolTip>
            </Box>
          );
        })}
      </Box>
    )}
  </>
);
